import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const ErrorSchema = z.object({ message: z.string() });

const ItemPathParamsSchema = z.object({ declarationId: z.string(), goodsShipmentItemId: z.string() });

export const ItemAiChatAnswerSchema = z.object({
  outcome: z
    .enum(['answer', 'clarification', 'unsupported', 'withheld'])
    .describe('answer, a clarifying question back to the user, a question outside the chat scope, or an answer withheld by a safety check'),
  /** markdown */
  text: z.string().describe('the answer, in markdown'),
  data: z.any().optional().describe('structured data behind the answer, when there is any'),
  caveats: z.array(z.string()).describe('limits of the answer the user should know about'),
});

export type ItemAiChatAnswer = z.infer<typeof ItemAiChatAnswerSchema>;

export const ItemAiChatMessageSchema = z.object({
  id: z.number(),
  authorId: z.string().describe('_id of the user who asked the question'),
  authorName: z.string().nullable().describe('name of the user who asked the question, null when the user could not be found'),
  isCurrentUser: z.boolean().describe('true when the calling user asked the question'),
  question: z.string(),
  answer: ItemAiChatAnswerSchema,
  createdAt: z.string().describe('ISO date and time the question was asked'),
});

export type ItemAiChatMessage = z.infer<typeof ItemAiChatMessageSchema>;

export const ItemAiChatSessionSchema = z.object({
  sessionId: z.string().describe('identifies the conversation; send it with every question'),
  item: z.any().describe('the risk analysis data of the goods item the chat answers about'),
  messages: z.array(ItemAiChatMessageSchema).describe('the conversation so far, oldest first'),
});

export const ItemAiChatTurnSchema = z.object({
  answer: ItemAiChatAnswerSchema,
  state: z.any().describe('internal state of the conversation'),
  message: ItemAiChatMessageSchema,
});

export type ItemAiChatTurn = z.infer<typeof ItemAiChatTurnSchema>;

export const itemAiChatContract = c.router({
  openItemAiChat: {
    method: 'POST',
    path: '/declarations/:declarationId/goods/:goodsShipmentItemId/ai-chat/sessions',
    pathParams: ItemPathParamsSchema,
    body: c.noBody(),
    responses: {
      200: ItemAiChatSessionSchema,
      404: ErrorSchema,
      502: ErrorSchema,
      503: ErrorSchema,
    },
  },
  sendItemAiChatMessage: {
    method: 'POST',
    path: '/declarations/:declarationId/goods/:goodsShipmentItemId/ai-chat/sessions/:sessionId/messages',
    pathParams: ItemPathParamsSchema.extend({ sessionId: z.string().uuid() }),
    body: z.object({ question: z.string().trim().min(1).max(2000) }),
    responses: {
      200: ItemAiChatTurnSchema,
      404: ErrorSchema,
      422: ErrorSchema,
      502: ErrorSchema,
      503: ErrorSchema,
    },
  },
});
